import { useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useRecentPagesStore } from '../lib/recent-pages-store';
import type { RecentEntityType } from '../types/command-palette';

/**
 * How long an entity view must stay on screen before it counts as "visited". Split-pane pages
 * (tasks, contacts) swap the selected entity as the user arrows down the list, which would
 * otherwise stamp a history entry for every row they skim past. Anything they linger on for
 * this long was almost certainly intentional.
 */
const SETTLE_MS = 800;

export interface RecordRecentPageInput {
  /** Stable per-entity id, e.g. `contact:42`. See RecentEntry.key. */
  key: string;
  type: RecentEntityType;
  label: string;
  description?: string;
  avatarUrl?: string;
}

/**
 * Records the entity currently on screen into this workspace's recent-pages history, for the
 * command palette's "Recent" group.
 *
 * Pass `null` (or `undefined`) while the entity is still loading — the hook records nothing
 * until it receives a real entry, so callers can hand it a query result directly without
 * guarding at the call site:
 *
 * ```ts
 * useRecordRecentPage(
 *   contact ? { key: `contact:${contact.id}`, type: 'contact', label: contact.fullName } : null
 * );
 * ```
 *
 * The navigable path is taken from the current location (pathname + search) rather than being
 * passed in, so view state like `?view=kanban` is restored when the user jumps back.
 */
export function useRecordRecentPage(input: RecordRecentPageInput | null | undefined) {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const location = useLocation();
  const record = useRecentPagesStore((s) => s.record);

  const path = `${location.pathname}${location.search}`;

  // Everything that would change what we'd persist. Comparing a flat signature rather than the
  // object identity means callers can build the input inline each render without re-recording.
  const signature = input
    ? [input.key, input.type, input.label, input.description ?? '', input.avatarUrl ?? '', path]
        .map((part) => String(part).replace(/\|/g, '\\|'))
        .join('|')
    : null;

  const lastRecorded = useRef<string | null>(null);

  // Callers build `input` inline on every render, so depending on it directly would restart the
  // settle timer forever and nothing would ever be written. The effect keys off `signature`,
  // which captures every field that affects the stored row, and reads the object itself
  // through this ref when the timer finally fires.
  const latestInput = useRef(input);
  latestInput.current = input;

  useEffect(() => {
    if (!workspaceId || !signature) return;
    if (lastRecorded.current === signature) return;

    const timer = setTimeout(() => {
      const entry = latestInput.current;
      if (!entry) return;

      lastRecorded.current = signature;
      record(workspaceId, {
        key: entry.key,
        type: entry.type,
        label: entry.label,
        description: entry.description,
        avatarUrl: entry.avatarUrl,
        path,
        visitedAt: Date.now(),
      });
    }, SETTLE_MS);

    return () => clearTimeout(timer);
  }, [workspaceId, signature, path, record]);
}
